import React, { useState, useCallback, useEffect, useRef, createContext } from 'react';
import { ReactFlow, applyNodeChanges, applyEdgeChanges, addEdge, Background, MiniMap, Controls } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { getLayoutedElements } from './getDagreLayout';
import CustomNodes from './CustomNodes/CustomNodes';
import LeftPanel from './LeftPanel';
import { useReactFlow } from '@xyflow/react';
import WorkflowTemplateOptionsMenu from '../RightContextMenu/WorkflowTemplateOptionsMenu';
import { useTranslation } from 'react-i18next';
import { treeActions } from "../../redux/slices/treeSlice";
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedNodes } from '../RightContextMenu/ContextActions/CopyAndPaste';
import { addNodesAndUpdateTransData, nodesValidation, removeNodesAndUpdateTransData } from '../../utils/nodesValidation';
import { getUpdatedData } from '../RightContextMenu/ContextActions/NewRecordOutbound';
import { eachDeep } from 'deepdash-es/standalone';
import { cloneDeep } from 'lodash';
import { insertNodeWithShift } from './helperFunctions';
import { RestartAlt } from '@mui/icons-material';

export const VisualWorkflowCopyPasteCtx = createContext();

const ReactFlowContainer = ({ initialNodes = [], initialEdges = [] }) => {
    const { t } = useTranslation();
    const reactFlowInstance = useReactFlow();
    const { getNodes, getIntersectingNodes } = useReactFlow();
    const dispatch = useDispatch();

    const [nodes, setNodes] = useState(initialNodes);
    const [edges, setEdges] = useState(initialEdges);
    const [menuOption, setMenuOption] = useState('');
    const [isCopied, setIsCopied] = useState({ copy: false, paste: false });
    const isFirstRender = useRef(true);
    const draggedNodeRef = useRef(null);

    const rightTreeSelectedNode = useSelector((state) => state.treeData.workflow?.rightTree?.tree?.body?.selectedNode);
    const { rightTree } = useSelector((state) => state.treeData.workflow);
    const translatedData = rightTree?.tree?.body?.translatedData;
    const templateHeader = rightTree?.tree?.templateHeader;
    const currentTab = rightTree?.currentTab || 'body';
    const parentScreen = 'workflow';

    const nodeTypes = {
        customNode: CustomNodes,
    };

    function updateNodePositionsInRedux(nodes){
        const positionMap = new Map();
        nodes.forEach((node) => {
            positionMap.set(node.id, node.position);
        });

        // Update node position in redux
        const updatedTranslatedData = eachDeep(cloneDeep(translatedData), (obj) => {
            if (obj?.alternateNodeId && positionMap.has(obj.alternateNodeId)) {
                obj.POSITIONX = positionMap.get(obj.alternateNodeId)?.x;
                obj.POSITIONY = positionMap.get(obj.alternateNodeId)?.y;
            }
        });

        dispatch(treeActions.setRightTreeTransData({
            screen: 'workflow',
            type: 'body',
            data: updatedTranslatedData,
        }))
    }

    function resetLayout() {
        const { nodes: n, edges: e } = getLayoutedElements(nodes, edges);
        updateNodePositionsInRedux(n);

        setNodes(n)
        setEdges(e)
    }

    // Update nodes and edges when props change
    useEffect(() => {
        // Use getDagreLayout.js function to get the nodes and edges

        if(initialNodes.length > 0 && initialNodes?.every(node => node?.position?.x === 0 && node?.position?.y === 0)) {
            
            const { nodes, edges } = getLayoutedElements(initialNodes, initialEdges);
            updateNodePositionsInRedux(nodes);
    
            setNodes(nodes);
            setEdges(edges);
        } else {
            if(initialNodes?.slice(1,)?.some(node => node?.position?.x === 0 && node?.position?.y === 0)) {
                const updatedNodes = insertNodeWithShift(initialNodes, initialEdges);
                updateNodePositionsInRedux(updatedNodes);
                setNodes(updatedNodes);
                setEdges(initialEdges);
            } else {
                setNodes(initialNodes);
                setEdges(initialEdges);
            }
        }
    }, [initialNodes]);

    const onNodesChange = useCallback(
        (changes) => {
            setNodes((nodesSnapshot) => applyNodeChanges(changes, nodesSnapshot));
        },
        [],
    );

    const onEdgesChange = useCallback(
        (changes) => {
            setEdges((edgesSnapshot) => applyEdgeChanges(changes, edgesSnapshot));
        },
        [],
    );

    const onConnect = useCallback(
        (params) => {
            setEdges((edgesSnapshot) => addEdge(params, edgesSnapshot));
        },
        [],
    );

    const onDragOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    const onDrop = (event) => {
        event.preventDefault();

        const type = event.dataTransfer.getData("application/reactflow");
        if (!type) return;

        const position = reactFlowInstance?.screenToFlowPosition({ x: event.clientX, y: event.clientY }); // convert viewport → flow coords

        // 🔍 detect if dropped on top of an existing node
        const targetNode = getNodes().find(node => {
            return (
                position.x > node.position.x &&
                position.x < node.position.x + (node.width || 150) &&   // width must exist OR fallback
                position.y > node.position.y &&
                position.y < node.position.y + (node.height || 60)      // height must exist OR fallback
            );
        });

        if (targetNode) {
            if(targetNode?.data?.data?.ICON_DISPLAY !== "CONDITION_IF" && type === "CONDITION_ELSE") {
                return;
            }

            dispatch(treeActions?.setRightTreeSelectedNode({
                screen: 'workflow',
                type: 'body',
                data: {
                    ...targetNode?.data?.data,
                },
            }));

            setTimeout(() => {
                setMenuOption(type);
            }, 100);
        } else if(rightTreeSelectedNode?.key) {
            if(rightTreeSelectedNode?.ICON_DISPLAY !== "CONDITION_IF" && type === "CONDITION_ELSE") {
                return;
            }
            setMenuOption(type);
        }
    };

    useEffect(() => {
        if(isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (rightTreeSelectedNode?.nodeId) {
            setNodes((currentNodes) => {
                const changes = [];
                
                // Create selection changes for all nodes
                currentNodes.forEach((node) => {
                    const shouldBeSelected = node?.id === rightTreeSelectedNode?.alternateNodeId;
                    if (node.selected !== shouldBeSelected) {
                        changes.push({ 
                            type: 'select', 
                            id: node.id, 
                            selected: shouldBeSelected 
                        });
                    }
                });
                
                // Apply all changes at once using applyNodeChanges
                return changes.length > 0 ? applyNodeChanges(changes, currentNodes) : currentNodes;
            });
        }
    }, [rightTreeSelectedNode?.key]);


    const updateNewData = useCallback((finalData, finalNode, newSelectedKeys) => {
        if (!templateHeader) return;
        
        const finalDataWithPosition = eachDeep(cloneDeep(finalData), (obj) => {
            if (obj?.alternateNodeId && obj.alternateNodeId === finalNode.alternateNodeId) {
                obj?.POSITIONX && delete obj?.POSITIONX;
                obj?.POSITIONY && delete obj?.POSITIONY;
            }
        });

        
        const newUpdatedData = getUpdatedData(
            finalDataWithPosition,
            templateHeader?.FILETYPE,
            null,
            true,
            currentTab
        );

        dispatch(
            treeActions.setRightTreeSelectedNode({
                screen: parentScreen,
                type: currentTab,
                data: {},
            })
        );
        setTimeout(() => {
            dispatch(
                treeActions.setRightTreeSelectedNode({
                    screen: parentScreen,
                    type: currentTab,
                    data: { ...finalNode },
                })
            );
        });
        dispatch(
            treeActions.setRightTreeTransData({
                screen: parentScreen,
                type: currentTab,
                data: newUpdatedData,
            })
        );
    }, [templateHeader, currentTab, dispatch]);

    const onNodeDragStart = useCallback((event, node) => {
        draggedNodeRef.current = node;
    }, []);

    const saveNodePosition = useCallback((node) => {
        const updatedTranslatedData = eachDeep(cloneDeep(translatedData), (obj) => {
            if (obj?.alternateNodeId && obj.alternateNodeId === node.id) {
                obj.POSITIONX = node.position.x;
                obj.POSITIONY = node.position.y;
            }
        });
        dispatch(treeActions.setRightTreeTransData({
            screen: 'workflow',
            type: 'body',
            data: updatedTranslatedData,
        }));
    }, [translatedData, dispatch]);

    const onNodeDragStop = useCallback((event, node) => {
        if (!draggedNodeRef.current || !translatedData || !templateHeader) {
            draggedNodeRef.current = null;
            dispatch(treeActions.setRightTreeSelectedNode({
                screen: 'workflow',
                type: 'body',
                data: node.data?.data,
            }));
            return;
        }

        const draggedNode = draggedNodeRef.current;
        const draggedNodeData = draggedNode.data?.data;
        
        if (!draggedNodeData?.key) {
            draggedNodeRef.current = null;
            dispatch(treeActions.setRightTreeSelectedNode({
                screen: 'workflow',
                type: 'body',
                data: node.data?.data,
            }));
            return;
        }

        // Use the node parameter which has the updated position after drag
        const intersections = getIntersectingNodes(node);
        const targetNode = intersections.find(intersection => 
            intersection.id !== node.id && 
            intersection.data?.data?.key
        );

        if (targetNode && targetNode.data?.data) {
            const targetNodeData = targetNode.data.data;
            const draggedNodes = getSelectedNodes([draggedNodeData.key], translatedData, true) || [];
            const draggedNodeKeys = draggedNodes.map((e) => e?.key);
            
            const { updatedData, removedNodes } = removeNodesAndUpdateTransData(
                translatedData,
                draggedNodeKeys,
                parentScreen,
                templateHeader?.FILETYPE,
                true,
                currentTab
            );

            let updatedTargetNode = null;
            const findUpdatedTarget = (data) => {
                data.forEach(node => {
                    if (node.nodeId === targetNodeData.nodeId && node.ICON_DISPLAY === targetNodeData.ICON_DISPLAY) {
                        updatedTargetNode = node;
                        return;
                    }
                    if (node.children) findUpdatedTarget(node.children);
                });
            };
            findUpdatedTarget(updatedData);
            
            const finalTargetNode = updatedTargetNode || targetNodeData;
            
            const { errorMessage, modalMessage, isInside } = nodesValidation(
                finalTargetNode,
                removedNodes,
                currentTab,
                true,
                templateHeader?.FILETYPE,
                updatedData,
                parentScreen
            );

            if (errorMessage) {
                draggedNodeRef.current = null;
                return;
            }

            if (modalMessage) {
                const selectedNodes = getSelectedNodes([finalTargetNode.key], updatedData, true);
                const destinationNode = selectedNodes?.length === 2 ? selectedNodes : (selectedNodes?.[0] || finalTargetNode);
                const insertInside = false;
                
                const { translatedData: finalData, selectedNode: finalNode, newSelectedKeys } = addNodesAndUpdateTransData(
                    updatedData,
                    removedNodes,
                    Array.isArray(destinationNode) ? destinationNode[0] : destinationNode,
                    insertInside,
                    templateHeader?.FILETYPE,
                    currentTab,
                    parentScreen,
                    true
                );
                updateNewData(finalData, finalNode, newSelectedKeys);
            } else {
                const { translatedData: finalData, selectedNode: finalNode, newSelectedKeys } = addNodesAndUpdateTransData(
                    updatedData,
                    removedNodes,
                    finalTargetNode,
                    isInside,
                    templateHeader?.FILETYPE,
                    currentTab,
                    parentScreen,
                    true
                );
                updateNewData(finalData, finalNode, newSelectedKeys);
            }
        } else {
            dispatch(treeActions.setRightTreeSelectedNode({
                screen: 'workflow',
                type: 'body',
                data: node.data?.data,
            }));
            saveNodePosition(node);
        }

        draggedNodeRef.current = null;
    }, [translatedData, templateHeader, currentTab, dispatch, getIntersectingNodes, updateNewData]);

    const handleNodeDrag = useCallback((event, node) => {
        // Keep this for visual feedback if needed
    }, []);

    return (
        <VisualWorkflowCopyPasteCtx.Provider value={{ isCopied, setIsCopied }}>
            <div style={{ width: '100vw', height: '100vh', display: 'flex' }}>
                <LeftPanel />
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onNodeDragStart={onNodeDragStart}
                    onNodeDragStop={onNodeDragStop}
                    minZoom={0.1}
                    maxZoom={1.5}
                    nodeTypes={nodeTypes}
                    onNodeDrag={handleNodeDrag}
                >
                    <Background />
                    <Controls position='top-left'>
                        <button type="button" 
                        className="react-flow__controls-button react-flow__controls-interactive" 
                        title="Reset Layout" aria-label="Reset Layout" onClick={() => resetLayout()}>
                            <RestartAlt sx={{ fontSize: 18, maxWidth: "16px !important", maxHeight: "16px !important" }}/>
                        </button>
                    </Controls>
                    <MiniMap position="bottom-left" />
                </ReactFlow>
                <WorkflowTemplateOptionsMenu setMenuOptionViaProp={menuOptionMapping[menuOption] ? t(menuOptionMapping[menuOption]) : ''} resetMenuOption={() => setMenuOption('')} />
            </div>
        </VisualWorkflowCopyPasteCtx.Provider>
    )
}

export default ReactFlowContainer;

const menuOptionMapping = {
    'GET_DATA': 'MNU_NEW_GET_DATA',
    'TRANSLATE': 'MNU_NEW_TRANSLATE',
    'SEND_DATA': 'MNU_NEW_SEND_DATA',
    'VARIABLE_ITEM': 'MNU_NEW_VAR',
    'LOOP_ITEM': 'MNU_NEW_LOOP',
    'CONDITION_IF': 'MNU_NEW_ITEM_LIST',
    'CONDITION_ELSE': 'MNU_NEW_ELSE_BLOCK',
    'CALL_PLUGIN': 'MNU_NEW_PLUG',
    'CALL_WORKFLOW': 'MNU_NEW_CALL_WF',
    'TRADING_PARTNER_LOOKUP': 'MNU_NEW_TP_LOOKUP',
    'ACTION': 'MNU_USE_ACTION',
    'COMMENT': 'MNU_NEW_COMMENT',
    'ERROR_HANDLER': 'MNU_NEW_ERR_HANDLER',
    'TRACKING_REPORT_ITEM': 'MNU_NEW_TRACKING_REPORT_ITEM',
}